import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { toast } from '../hooks/use-toast';

const API = '/api';

const TYPE_CONFIG = {
  new_transaction: { color: 'bg-blue-400',  label: 'New Challan'   },
  approved:        { color: 'bg-emerald-500', label: 'Approved'     },
  discrepancy:     { color: 'bg-red-500',    label: 'Discrepancy'   },
  default:         { color: 'bg-slate-400',  label: 'Notification'  },
};

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const buttonRef = useRef(null);
  const panelRef  = useRef(null);
  const wsRef     = useRef(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  useEffect(() => {
    let ws;
    let reconnectTimer;

    const connect = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const token = session.access_token;
        const wsBase = process.env.REACT_APP_WS_URL ||
          `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
        ws = new WebSocket(`${wsBase}/ws?token=${token}`);
        wsRef.current = ws;

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'notification') {
              setNotifications(prev => [msg.payload, ...prev.slice(0, 49)]);
              if (!open) {
                toast({ title: msg.payload.title || 'New notification', description: msg.payload.body || '' });
              }
            }
          } catch (_) {}
        };

        ws.onclose = () => { reconnectTimer = setTimeout(connect, 5000); };
      } catch (_) {}
    };

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        panelRef.current  && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleBellClick = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const margin = 8;
      const maxPanelHeight = 448; // header (~48px) + max-h-96 list (384px) + borders
      const dropdownWidth = Math.min(320, window.innerWidth - margin * 2);

      // Horizontal: right-align to bell, clamp to both viewport edges
      let left = rect.right - dropdownWidth;
      if (left < margin) left = margin;
      if (left + dropdownWidth > window.innerWidth - margin) {
        left = window.innerWidth - margin - dropdownWidth;
      }

      // Vertical: open below the bell; if it would clip the bottom edge,
      // open above the bell instead; finally clamp within the viewport.
      let top = rect.bottom + margin;
      if (top + maxPanelHeight > window.innerHeight - margin) {
        top = rect.top - margin - maxPanelHeight;
      }
      if (top < margin) top = margin;

      setDropdownPos({ top, left, width: dropdownWidth });
    }
    setOpen(o => !o);
    if (!open) fetchNotifications();
  };

  const unread = notifications.filter(n => !n.read_at).length;

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch (_) {}
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    } catch (_) {}
  };

  return (
    <>
      <button
        ref={buttonRef}
        className="relative p-1.5 rounded-lg transition-colors"
        style={{ color: 'hsl(var(--primary-foreground) / 0.7)' }}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'hsl(var(--primary-foreground) / 0.1)'; e.currentTarget.style.color = 'hsl(var(--primary-foreground))'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'hsl(var(--primary-foreground) / 0.7)'; }}
        onClick={handleBellClick}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full text-white text-[8px] font-bold flex items-center justify-center leading-none"
            style={{ backgroundColor: 'hsl(var(--klaro-clarity-blue))' }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="bg-card rounded-xl shadow-2xl border border-border overflow-hidden"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width || 320,
            maxHeight: `calc(100vh - ${16}px)`,
            zIndex: 9999,
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-semibold text-foreground text-sm">Notifications</span>
            {unread > 0 && (
              <button
                className="text-xs hover:underline"
                style={{ color: 'hsl(var(--klaro-clarity-blue))' }}
                onClick={markAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <Bell className="h-6 w-6 mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : (
              notifications.map(n => {
                const cfg = TYPE_CONFIG[n.notification_type] || TYPE_CONFIG.default;
                return (
                  <button
                    key={n.id}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted transition-colors ${!n.read_at ? 'bg-accent/10' : ''}`}
                    onClick={() => markRead(n.id)}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 h-2 w-2 rounded-full flex-shrink-0 ${!n.read_at ? cfg.color : 'bg-muted-foreground/30'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium leading-tight ${!n.read_at ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.body}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
};
